import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { AlertTriangle, FileText, Send, CheckCircle } from 'lucide-react';

interface ConflictVersion {
  id: string;
  version_number: number;
  version_type: string;
  created_at: string;
}

interface ConflictData {
  sent_versions: ConflictVersion[];
  approved_versions: ConflictVersion[];
}

interface StatusUpgradeConflictDialogProps {
  isOpen: boolean;
  onClose: () => void;
  conflicts: ConflictData;
  newStatus: 'sent' | 'approved';
  onResolve: (resolution: {
    sent_versions?: Array<{ id: string; action: 'cancel' | 'reject' | 'leave' }>;
    approved_versions?: Array<{ id: string; action: 'cancel' | 'reject' | 'leave' }>;
  }) => void;
}

const StatusUpgradeConflictDialog: React.FC<StatusUpgradeConflictDialogProps> = ({
  isOpen,
  onClose,
  conflicts,
  newStatus,
  onResolve
}) => {
  const [sentResolutions, setSentResolutions] = useState<Record<string, 'cancel' | 'reject' | 'leave'>>({});
  const [approvedResolutions, setApprovedResolutions] = useState<Record<string, 'cancel' | 'reject' | 'leave'>>({});

  const hasConflicts = conflicts.sent_versions.length > 0 || conflicts.approved_versions.length > 0;
  const hasApprovedConflicts = conflicts.approved_versions.length > 0;

  // Check if all approved conflicts have a resolution (mandatory - no "leave" allowed)
  const allApprovedResolved = conflicts.approved_versions.every(
    version => approvedResolutions[version.id] && approvedResolutions[version.id] !== 'leave'
  );

  // Can only proceed if all approved conflicts are resolved
  const canProceed = !hasApprovedConflicts || allApprovedResolved;

  // Reset resolutions when dialog opens
  useEffect(() => {
    if (isOpen) {
      setSentResolutions({});
      setApprovedResolutions({});
    }
  }, [isOpen]);

  const handleResolve = () => {
    const resolution: any = {};

    if (conflicts.sent_versions.length > 0) {
      resolution.sent_versions = conflicts.sent_versions
        .map(version => ({
          id: version.id,
          action: sentResolutions[version.id] || 'leave'
        }))
        .filter(r => r.action !== 'leave');
    }

    if (conflicts.approved_versions.length > 0) {
      resolution.approved_versions = conflicts.approved_versions
        .map(version => ({
          id: version.id,
          action: approvedResolutions[version.id] || 'leave'
        }))
        .filter(r => r.action !== 'leave');
    }

    onResolve(resolution);
  };

  const getVersionIcon = (versionType: string) => {
    return versionType === 'change_order' ? <FileText className="h-4 w-4" /> : <Send className="h-4 w-4" />;
  };

  const getVersionLabel = (version: ConflictVersion) => {
    const label = version.version_type === 'change_order' 
      ? `Change Order v${version.version_number}`
      : `Version ${version.version_number}`;
    
    return label;
  };

  // Action buttons for sent versions (leave as-is is allowed)
  const SentActionButtons = ({ versionId, currentAction, onChange }: {
    versionId: string;
    currentAction: string;
    onChange: (action: 'cancel' | 'reject' | 'leave') => void;
  }) => (
    <div className="flex gap-2">
      <Button
        variant={currentAction === 'cancel' ? 'default' : 'outline'}
        size="sm"
        onClick={() => onChange('cancel')}
      >
        Cancel
      </Button>
      <Button
        variant={currentAction === 'reject' ? 'default' : 'outline'}
        size="sm"
        onClick={() => onChange('reject')}
      >
        Reject
      </Button>
      <Button
        variant={currentAction === 'leave' ? 'default' : 'outline'}
        size="sm"
        onClick={() => onChange('leave')}
      >
        Leave as is
      </Button>
    </div>
  );

  // Action buttons for approved versions (mandatory resolution - no "leave as-is")
  const ApprovedActionButtons = ({ versionId, currentAction, onChange }: {
    versionId: string;
    currentAction: string;
    onChange: (action: 'cancel' | 'reject') => void;
  }) => {
    const needsResolution = !currentAction || currentAction === 'leave';
    
    return (
      <div className={`flex gap-2 ${needsResolution ? 'ring-2 ring-destructive ring-offset-2 rounded-md p-1' : ''}`}>
        <Button
          variant={currentAction === 'cancel' ? 'default' : 'outline'}
          size="sm"
          onClick={() => onChange('cancel')}
        >
          Cancel
        </Button>
        <Button
          variant={currentAction === 'reject' ? 'default' : 'outline'}
          size="sm"
          onClick={() => onChange('reject')}
        >
          Reject
        </Button>
      </div>
    );
  };

  if (!hasConflicts) {
    return null;
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-500" />
            Status Upgrade Conflicts Detected
          </DialogTitle>
          <DialogDescription>
            There are existing {newStatus === 'sent' ? 'sent' : 'approved'} versions for this estimate. 
            What would you like to do with them?
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Alert - different message for approved conflicts */}
          {hasApprovedConflicts ? (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                <strong>Action Required:</strong> Only one approved version can exist at a time. 
                You must cancel or reject existing approved versions before proceeding.
              </AlertDescription>
            </Alert>
          ) : (
            <Alert>
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                Multiple versions with the same status can cause confusion. Consider updating the status 
                of existing versions to maintain clarity.
              </AlertDescription>
            </Alert>
          )}

          {/* Sent Versions Conflicts */}
          {conflicts.sent_versions.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Send className="h-4 w-4 text-blue-600" />
                <h4 className="font-medium">Existing Sent Versions</h4>
                <Badge variant="secondary">{conflicts.sent_versions.length}</Badge>
              </div>
              
              <div className="space-y-3">
                {conflicts.sent_versions.map((version) => (
                  <div key={version.id} className="flex items-center justify-between p-3 border rounded-lg">
                    <div className="flex items-center gap-3">
                      {getVersionIcon(version.version_type)}
                      <div>
                        <div className="font-medium">{getVersionLabel(version)}</div>
                        <div className="text-sm text-muted-foreground">
                          Created {format(new Date(version.created_at), 'dd MMMM yyyy')}
                        </div>
                      </div>
                    </div>
                    <SentActionButtons
                      versionId={version.id}
                      currentAction={sentResolutions[version.id] || 'leave'}
                      onChange={(action) => setSentResolutions(prev => ({ ...prev, [version.id]: action }))}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Approved Versions Conflicts - Mandatory Resolution */}
          {conflicts.approved_versions.length > 0 && (
            <div className="space-y-3">
              {conflicts.sent_versions.length > 0 && <Separator />}
              
              <div className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <h4 className="font-medium">Existing Approved Versions</h4>
                <Badge variant="destructive">{conflicts.approved_versions.length} - Resolution Required</Badge>
              </div>
              
              <div className="space-y-3">
                {conflicts.approved_versions.map((version) => {
                  const needsResolution = !approvedResolutions[version.id] || approvedResolutions[version.id] === 'leave';
                  return (
                    <div 
                      key={version.id} 
                      className={`flex items-center justify-between p-3 border rounded-lg ${
                        needsResolution ? 'border-destructive bg-destructive/5' : ''
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        {getVersionIcon(version.version_type)}
                        <div>
                          <div className="font-medium">{getVersionLabel(version)}</div>
                          <div className="text-sm text-muted-foreground">
                            Created {format(new Date(version.created_at), 'dd MMMM yyyy')}
                          </div>
                          {needsResolution && (
                            <div className="text-sm text-destructive font-medium mt-1">
                              Select an action to proceed
                            </div>
                          )}
                        </div>
                      </div>
                      <ApprovedActionButtons
                        versionId={version.id}
                        currentAction={approvedResolutions[version.id] || ''}
                        onChange={(action) => setApprovedResolutions(prev => ({ ...prev, [version.id]: action }))}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button 
            onClick={handleResolve}
            disabled={!canProceed}
            title={!canProceed ? 'Please resolve all approved version conflicts first' : ''}
          >
            Proceed with Resolution
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default StatusUpgradeConflictDialog;